// Shared history list for shoots and events. Storage keys remain type-specific
// for compatibility; each row uses {n, date, location, video_id?, notes}.
import React from "react";
import { Plus, Trash2, Film, Calendar, ArrowUpRight } from "lucide-react";

export default function ShootsList({ shoots, onChange, kindLabel = "Shoot", catalogVideos = [], catalogState = "loaded" }) {
  const list = shoots || [];
  const sorted = [...list].sort((a, b) => (a.n || 0) - (b.n || 0));

  const add = () => {
    const nextN = (list[list.length - 1]?.n || list.length) + 1;
    onChange([...list, {
      id: `s_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      n: nextN,
      date: "",
      location: "",
      video_id: "",
      notes: "",
    }]);
  };
  const update = (id, patch) => onChange(list.map(s => s.id === id ? { ...s, ...patch } : s));
  const remove = (id) => onChange(list.filter(s => s.id !== id));

  const ordinal = (n) => {
    const s = ["th","st","nd","rd"], v = n % 100;
    return n + (s[(v-20)%10] || s[v] || s[0]);
  };
  const totalLabel = kindLabel === "Event" ? "Events Total" : "Total";

  return (
    <div className="mt-4 space-y-4" data-testid="crm-shoots">
      {/* Header Section */}
      <div className="border-t pt-4" style={{ borderColor: "rgba(255,16,240,0.2)" }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider" 
            style={{ color: "#FF10F0" }}>
            <Film size={12} /> {kindLabel.toUpperCase()} HISTORY
          </div>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" 
            style={{ background: "rgba(255,16,240,0.1)", color: "#FF10F0" }}>
            {list.length} {totalLabel}
          </span>
        </div>
      </div>

      {/* Shoots List Section */}
      <div className="space-y-3">
        {sorted.map((s) => (
          <div key={s.id} className="rounded-xl border p-4" data-testid={`crm-shoot-${s.id}`}
            style={{ 
              borderColor: "rgba(255,16,240,0.2)", 
              background: "linear-gradient(135deg, rgba(255,255,255,0.02) 0%, rgba(255,16,240,0.02) 100%)"
            }}>
            {/* Shoot Number Badge */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold px-3 py-1 rounded-full"
                style={{ background: "rgba(255,16,240,0.15)", color: "#FF10F0" }}>
                {ordinal(s.n)} {kindLabel}
              </span>
              <button type="button" onClick={() => remove(s.id)}
                data-testid={`crm-shoot-del-${s.id}`}
                className="text-zinc-600 hover:text-pink-400 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>

            {/* Input Fields Grid */}
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider mb-2 text-purple-300">Date</div>
                <input type="date" value={s.date || ""} data-testid={`crm-shoot-date-${s.id}`}
                  onChange={(e) => update(s.id, { date: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-white text-sm"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,16,240,0.2)" }} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider mb-2 text-purple-300">Location</div>
                <input value={s.location || ""} data-testid={`crm-shoot-location-${s.id}`}
                  onChange={(e) => update(s.id, { location: e.target.value })}
                  placeholder="Milan studio…" 
                  className="w-full px-3 py-2 rounded-lg text-white text-sm"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,16,240,0.2)" }} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider mb-2 text-purple-300">Notes</div>
                <input value={s.notes || ""} data-testid={`crm-shoot-notes-${s.id}`}
                  onChange={(e) => update(s.id, { notes: e.target.value })}
                  placeholder="Quick note (scene title, vibes…)" 
                  className="w-full px-3 py-2 rounded-lg text-white text-sm"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,16,240,0.2)" }} />
              </div>
              {kindLabel.toLowerCase() === "shoot" && (() => {
                const linkedVideo = catalogVideos.find((video) => video.id === s.video_id);
                const missingOptionLabel = catalogState === "loaded"
                  ? "Linked video unavailable"
                  : catalogState === "loading"
                    ? "Loading catalog…"
                    : catalogState === "denied"
                      ? "Catalog access unavailable"
                      : "Catalog unavailable";
                const statusMessage = catalogState === "loading"
                  ? "Loading catalog videos…"
                  : catalogState === "denied"
                    ? "Catalog access denied. The saved link is retained."
                    : catalogState === "error"
                      ? "Catalog could not be loaded. The saved link is retained."
                      : catalogVideos.length === 0
                        ? "No catalog videos available."
                        : "";

                return (
                <div className="sm:col-span-3">
                  <label htmlFor={`crm-shoot-video-${s.id}`} className="block text-[10px] font-bold uppercase tracking-wider mb-2 text-purple-300">Catalog video</label>
                  <select id={`crm-shoot-video-${s.id}`} value={s.video_id || ""} data-testid={`crm-shoot-video-${s.id}`}
                    onChange={(e) => update(s.id, { video_id: e.target.value })}
                    className="w-full min-h-11 px-3 py-2 rounded-lg text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,16,240,0.2)" }}>
                    <option value="" className="bg-zinc-900">No video linked</option>
                    {s.video_id && !linkedVideo && (
                      <option value={s.video_id} className="bg-zinc-900">{missingOptionLabel}</option>
                    )}
                    {catalogVideos.map((video) => (
                      <option key={video.id} value={video.id} className="bg-zinc-900">{video.title || "Untitled video"}</option>
                    ))}
                  </select>
                  {linkedVideo && (
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs" data-testid={`crm-shoot-linked-video-${s.id}`}>
                      <span className="text-zinc-300">Selected: <span className="font-medium text-white">{linkedVideo.title || "Untitled video"}</span></span>
                      <a href={`/crm/catalog?videoId=${encodeURIComponent(s.video_id)}`}
                        data-testid={`crm-shoot-open-video-${s.id}`}
                        aria-label={`Open ${linkedVideo.title || "video"} in Catalog`}
                        className="inline-flex min-h-11 items-center gap-1 rounded px-2 font-medium text-pink-300 underline decoration-pink-300/50 underline-offset-2 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pink-300">
                        <ArrowUpRight size={13} aria-hidden="true" /> Open in Catalog
                      </a>
                    </div>
                  )}
                  {s.video_id && !linkedVideo && (
                    <div className="mt-1 text-xs text-amber-200" role="status" data-testid={`crm-shoot-video-fallback-${s.id}`}>
                      {catalogState === "loaded" ? "Linked video unavailable" : `${missingOptionLabel}. Saved link retained.`}
                    </div>
                  )}
                  {!s.video_id && statusMessage && (
                    <div className="mt-1 text-xs text-zinc-400" role={catalogState === "denied" || catalogState === "error" ? "alert" : "status"}>
                      {statusMessage}
                    </div>
                  )}
                </div>
                );
              })()}
            </div>
          </div>
        ))}
        
        {/* Empty State */}
        {list.length === 0 && (
          <div className="text-center py-6 px-4 rounded-xl border" 
            style={{ borderColor: "rgba(255,16,240,0.1)", background: "rgba(255,255,255,0.02)" }}>
            <Film size={24} className="mx-auto mb-2 opacity-30" style={{ color: "#FF10F0" }} />
            <div className="text-sm" style={{ color: "var(--gio-muted-3)" }}>
              No {kindLabel.toLowerCase()}s logged yet
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--gio-muted-3)" }}>
              Click below to add your first one
            </div>
          </div>
        )}
      </div>

      {/* Action Section */}
      <div className="flex items-center justify-between pt-2 border-t" style={{ borderColor: "rgba(255,16,240,0.1)" }}>
        <button type="button" onClick={add} data-testid="crm-shoot-add"
          className="px-4 py-2 rounded-lg text-xs font-medium inline-flex items-center gap-2 transition-all hover:scale-105"
          style={{ 
            background: "rgba(255,16,240,0.1)", 
            color: "#FF10F0",
            border: "1px solid rgba(255,16,240,0.3)"
          }}>
          <Plus size={13} /> Log {ordinal(list.length + 1)} {kindLabel.toLowerCase()}
        </button>
        <div className="inline-flex items-center gap-1.5 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>
          <Calendar size={9} /> Auto-synced with status
        </div>
      </div>
    </div>
  );
}
